import IconNetwork from "@common/components/icons/IconNetwork";
import AccordionSection from "@common/components/thirdParty/mantine/accordion/AccordionSection";
import TitleWithIcon from "@common/components/thirdParty/mantine/text/TitleWithIcon";
import { persistUTMQueryParamsToString } from "@common/utils/router";
import {
  ActionIcon,
  Anchor,
  Button,
  Card,
  CardProps,
  Code,
  CopyButton,
  Flex,
  Text,
  Tooltip,
} from "@mantine/core";
import { useRouter } from "next/router";
import { ReactElement } from "react";
import { useStyles } from "./TokenSubscriptionSection.styled";
import { IPurchaseCardProps } from "../../types";
import Copy from "@common/components/icons/Copy";
import { isCanceledOrDeleted } from "@stores/purchases/utils";
import IconCheckRounded from "@common/components/icons/IconCheckRounded";

export function TokenSubscriptionSectionCompact({
  purchase,
  ...cardProps
}: Pick<IPurchaseCardProps, "purchase"> & Omit<CardProps, "children">) {
  const { classes } = useStyles();

  const router = useRouter();

  const { token } = purchase;

  if (!token) {
    return null;
  }

  const isPurchaseCanceledOrDeleted = isCanceledOrDeleted(purchase);

  return (
    <Card
      data-testid="PurchaseCard_licence-key"
      withBorder
      px="xs"
      py="0.25rem"
      bg="gray.1"
      {...cardProps}
    >
      <Flex gap="xs">
        <Flex gap="xs" align="baseline">
          <Anchor
            href={persistUTMQueryParamsToString(
              router,
              "https://www.metabase.com/docs/latest/enterprise-guide/activating-the-enterprise-edition",
            )}
            target="_blank"
            rel="noreferrer"
            className={classes.keyLabel}
          >
            License key:
          </Anchor>

          <Text
            fz="sm"
            data-testid="PurchaseCard_licence-key_code"
            className={classes.codeCompact}
          >
            {token}
          </Text>
        </Flex>

        {!isPurchaseCanceledOrDeleted && (
          <CopyButton value={token}>
            {({ copied, copy }) => (
              <Tooltip
                label={copied ? "Copied" : "Copy"}
                withArrow
                withinPortal
                position="top"
              >
                <Flex direction="column" ml="auto" h="1lh" justify="center">
                  <ActionIcon onClick={copy}>
                    {copied ? (
                      <IconCheckRounded size={16} />
                    ) : (
                      <Copy size={16} />
                    )}
                  </ActionIcon>
                </Flex>
              </Tooltip>
            )}
          </CopyButton>
        )}
      </Flex>
    </Card>
  );
}

export default function TokenSubscriptionSection({
  purchase,
}: Pick<IPurchaseCardProps, "purchase">): ReactElement {
  const { classes } = useStyles();

  const router = useRouter();

  const { id, token } = purchase;

  if (!token) {
    throw new Error(`TokenSubscriptionSection, token is undefined for ${id}`);
  }

  const isPurchaseCanceledOrDeleted = isCanceledOrDeleted(purchase);

  return (
    <AccordionSection data-testid="PurchaseCard_licence-key">
      <TitleWithIcon
        icon={<IconNetwork />}
        title="License Key"
        order={3}
        size="h6"
      />

      {!isPurchaseCanceledOrDeleted && (
        <Text fz="sm" mt="xs">
          To get started,{" "}
          <Anchor
            href={persistUTMQueryParamsToString(
              router,
              "https://www.metabase.com/docs/latest/enterprise-guide/activating-the-enterprise-edition",
            )}
            target="_blank"
            rel="noreferrer"
          >
            follow these instructions
          </Anchor>{" "}
          and enter the token below:
        </Text>
      )}

      <Flex
        align="center"
        direction={{ base: "column", xs: "row" }}
        justify="stretch"
        wrap="nowrap"
        mt="xs"
      >
        <Code
          data-testid="PurchaseCard_licence-key_code"
          className={classes.code}
        >
          {token}
        </Code>

        {!isPurchaseCanceledOrDeleted && (
          <CopyButton value={token}>
            {({ copied, copy }) => (
              <Button
                className={classes.copy}
                leftIcon={<Copy size={20} color="white" />}
                ml={{ base: 0, xs: "xs" }}
                mt={{ base: "xs", xs: 0 }}
                w={{ base: "100%", xs: "auto" }}
                onClick={copy}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            )}
          </CopyButton>
        )}
      </Flex>
    </AccordionSection>
  );
}
